import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { platform } from '../lib/api.js'
import { Badge, Card, Empty, Spinner, toast } from '../components/ui/index.js'
import { useI18n } from '../lib/i18n.js'

/**
 * Plataforma → Mensagens de contato.
 *
 * Caixa de entrada do formulário da página institucional (asknamba.com):
 * cada envio substitui um e-mail que chegaria na caixa desativada — aqui
 * fica o triage, com flag "tratada" para o que já foi respondido.
 */
export function PlatformContacts() {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const { data: messages = [], isLoading } = useQuery({
    queryKey: ['platform-contact'],
    queryFn: platform.contactMessages,
  })

  const toggle = useMutation({
    mutationFn: platform.contactSetHandled,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['platform-contact'] }),
    onError: () => toast.error(t('admin.contact.toggle_error')),
  })

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Card title={t('admin.contact.title')} description={t('admin.contact.desc')}>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner className="w-5 h-5 text-indigo-400" />
          </div>
        ) : messages.length === 0 ? (
          <Empty
            title={t('admin.contact.empty_title')}
            description={t('admin.contact.empty_hint')}
          />
        ) : (
          <ul className="divide-y divide-gray-100">
            {messages.map((msg) => (
              <li key={msg.id} className="py-4">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {msg.handledAt ? (
                        <Badge color="green">{t('admin.contact.handled')}</Badge>
                      ) : (
                        <Badge color="yellow">{t('admin.contact.new')}</Badge>
                      )}
                      <Badge color="gray">{msg.locale}</Badge>
                      <span className="text-xs text-gray-400">
                        {new Date(msg.createdAt * 1000).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-800">
                      {msg.name} ·{' '}
                      <a className="text-indigo-600" href={`mailto:${msg.email}`}>
                        {msg.email}
                      </a>
                    </p>
                    <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">{msg.message}</p>
                  </div>
                  <button
                    onClick={() => toggle.mutate(msg.id)}
                    disabled={toggle.isPending}
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-800 whitespace-nowrap disabled:opacity-50"
                  >
                    {t(msg.handledAt ? 'admin.contact.reopen' : 'admin.contact.mark_handled')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}
